/**
 * DDGenerativeResults — SDUI-enhanced results component
 *
 * Renders analysis results via ModernResultsFull (proven, polished).
 * Fetches a lightweight content manifest from the generate-ui-spec CF Worker
 * endpoint (Accept-Version: v3) to personalize narrator text.
 *
 * Architecture (Option B+, multi-model consensus 8-9/10):
 * - ModernResultsFull is the renderer
 * - LLM generates content manifests, not component trees
 * - Structural UI (feedback, share, annotations) is NEVER LLM-generated
 * - Manifest fetch is fire-and-forget (3s timeout, silent failure)
 *
 * Ref: .claude/docs/SDUI_ADOPTION_GUIDE.md
 * Task: P7.3
 */

import React, { useState, useEffect, useRef } from "react";
import type {
  DramaAnalysisResponse,
  MessageContext,
  ContentManifest,
} from "@dramadetector/shared";
import { getEdgeFunctionUrl, authenticatedFetch } from "@dramadetector/api-client";
import { ModernResultsFull } from "./ModernResultsFull";
import type { ResultsFunnelSurface } from "../../hooks/useResultsFunnelAnalytics";
import type { RevisionTrackingConfig } from "./inline-annotations/InlineMessageDisplay";
import { useManifestInstrumentation } from "./useManifestInstrumentation";

// ---------------------------------------------------------------------------
// useContentManifest — fire-and-forget manifest fetcher
//
// Calls generate-ui-spec with Accept-Version: v3 to get a lightweight
// content manifest (~200 tokens). 3s timeout, silent failure.
// The results screen renders immediately; manifest overlays when ready.
// ---------------------------------------------------------------------------

const MANIFEST_TIMEOUT_MS = 3000;

function useContentManifest(
  analysis: DramaAnalysisResponse | undefined
): { manifest: ContentManifest | null; isLoading: boolean } {
  const [manifest, setManifest] = useState<ContentManifest | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // P6.1: Check for bundled manifest first (eliminates second round-trip)
  const bundledManifest = (analysis as any)?.contentManifest as
    | ContentManifest
    | null
    | undefined;

  // eslint-disable-next-line local-rules/no-race-conditions -- single manifest fetch effect with AbortController cleanup; one writer for manifest/loading state per analysis id
  // eslint-disable-next-line local-rules/no-race-conditions -- single manifest bootstrap effect with AbortController cleanup; repeated setManifest calls are ordered fallbacks within one effect, not competing effects.
  useEffect(() => {
    if (!analysis?.id) return;

    // If the analyze-drama response already includes a manifest, use it directly
    if (bundledManifest) {
      setManifest(bundledManifest);
      return;
    }

    // Fallback: fetch manifest separately via direct fetch (backwards compatibility)
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    const timeout = setTimeout(() => controller.abort(), MANIFEST_TIMEOUT_MS);

    // Sentry DRAMA-EDGE-17 root cause: this previously used raw fetch(), which
    // bypassed authenticatedFetch's Bearer-injection + cold-load pre-gate. Every
    // call landed at the worker auth middleware with hasAuthHeader=false and
    // 401'd, polluting Sentry and dropping the manifest silently for every
    // authenticated user reloading a results page. Use authenticatedFetch so the
    // Bearer is attached AND the pre-gate waits for AuthContext bootstrap.
    authenticatedFetch(getEdgeFunctionUrl('generate-ui-spec'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Version': 'v3',
      },
      body: JSON.stringify({ analysisId: analysis.id, analysisResult: analysis }),
      signal: controller.signal,
    })
      .then((res) => {
        if (controller.signal.aborted) return null;
        if (!res.ok) return null;
        return res.json();
      })
      .then((data) => {
        if (controller.signal.aborted) return;
        if (data?.manifest) {
          setManifest(data.manifest as ContentManifest);
        }
      })
      .catch(() => {
        // Silent fail — screen works without manifest
      })
      .finally(() => {
        setIsLoading(false);
        clearTimeout(timeout);
      });

    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, [analysis, analysis?.id, bundledManifest]);

  return { manifest, isLoading };
}

// ---------------------------------------------------------------------------
// DDGenerativeResults
// ---------------------------------------------------------------------------

interface DDGenerativeResultsProps {
  analysis: DramaAnalysisResponse;
  message: string;
  context?: MessageContext;
  tierLevel: 1 | 2 | 3;
  tier?: "anonymous" | "free" | "drama_plus" | "dramax" | "family_member";
  t: (key: string, options?: Record<string, unknown>) => string;
  onNewAnalysis: () => void;
  onUpgrade?: () => void;
  l3AccordionsEnabled?: boolean;
  tracking?: RevisionTrackingConfig;
  analyticsSurface?: ResultsFunnelSurface;
  testID?: string;
}

/**
 * Renders ModernResultsFull with content manifest overrides.
 * The manifest provides an LLM-generated narratorOverride that replaces
 * the default narrator_intro when available.
 */
export function DDGenerativeResults({
  analysis,
  message,
  context,
  tierLevel,
  tier = "free",
  t,
  onNewAnalysis,
  onUpgrade,
  l3AccordionsEnabled,
  tracking,
  analyticsSurface = "results_page",
  testID = "dd-generative-results",
}: DDGenerativeResultsProps) {
  const { manifest } = useContentManifest(analysis);

  // P8.1: Instrument manifest fields for engagement measurement
  useManifestInstrumentation({
    manifest,
    analysisId: analysis?.id,
    tier: tier ?? "free",
  });

  return (
    <ModernResultsFull
      analysis={analysis}
      message={message}
      context={context}
      tierLevel={tierLevel}
      tier={tier}
      t={t}
      onNewAnalysis={onNewAnalysis}
      onUpgrade={onUpgrade}
      l3AccordionsEnabled={l3AccordionsEnabled}
      tracking={tracking}
      analyticsSurface={analyticsSurface}
      // Content manifest override: LLM-personalized narrator text
      streamedNarrator={manifest?.narratorOverride}
      testID={testID}
    />
  );
}
